import { NavigateFunction } from 'react-router-dom';

// Extract keys from the interface type
export const EMBED_PARAM_KEYS = ['embed', 'source', 'userAlias'] as const;

/**
 * Utility function to navigate while preserving embed parameters
 * @param navigate - React Router navigate function
 * @param path - The path to navigate to
 * @param preserveEmbedParams - Whether to preserve embed-related query parameters (default: true)
 */
export const navigateWithEmbedParams = (
  navigate: NavigateFunction,
  path: string,
  preserveEmbedParams: boolean = true,
  options?: { replace?: boolean }
): void => {
  if (!preserveEmbedParams) {
    navigate(path);
    return;
  }

  // Parse the target path to extract any existing parameters
  const [basePath, fragment] = path.split('#');
  const [pathWithoutParams, existingParamsString] = basePath.split('?');
  const existingParams = new URLSearchParams(existingParamsString || '');

  // Get current embed params and merge with existing params
  const embedParams = getEmbedParams();
  EMBED_PARAM_KEYS.forEach((key) => {
    const value = embedParams.get(key);
    if (value !== null) {
      existingParams.set(key, value);
    }
  });

  // Construct the final path
  const paramsString = existingParams.toString();
  const finalPath = paramsString
    ? `${pathWithoutParams}?${paramsString}${fragment ? '#' + fragment : ''}`
    : `${pathWithoutParams}${fragment ? '#' + fragment : ''}`;

  if (options) {
    navigate(finalPath, options);
  } else {
    navigate(finalPath);
  }
};

/**
 * Utility function to extract embed parameters from current URL
 * @returns URLSearchParams containing only embed parameters
 */
const getEmbedParams = (): URLSearchParams => {
  const currentParams = new URLSearchParams(window.location.search);
  const embedParams = new URLSearchParams();

  EMBED_PARAM_KEYS.forEach((key) => {
    const value = currentParams.get(key);
    if (value !== null) {
      embedParams.set(key, value);
    }
  });

  return embedParams;
};

/**
 * Utility function to get current embed parameters as a query string
 * @returns Query string with embed parameters (without leading ?)
 */
export const getEmbedParamsQueryString = (): string => {
  return getEmbedParams().toString();
};

/**
 * Utility function to append embed parameters to a given path
 * @param path - The base path
 * @returns Path with embed parameters appended
 */
export const appendEmbedParamsToPath = (path: string): string => {
  const [basePath, fragment] = path.split('#');
  const [pathWithoutParams, existingParamsString] = basePath.split('?');
  const existingParams = new URLSearchParams(existingParamsString || '');

  // Get current embed params and merge with existing params
  const embedParams = getEmbedParams();
  EMBED_PARAM_KEYS.forEach((key) => {
    const value = embedParams.get(key);
    if (value !== null) {
      existingParams.set(key, value);
    }
  });

  // Construct the final path
  const paramsString = existingParams.toString();
  return paramsString
    ? `${pathWithoutParams}?${paramsString}${fragment ? '#' + fragment : ''}`
    : `${pathWithoutParams}${fragment ? '#' + fragment : ''}`;
};
